import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckOutlined } from "@ant-design/icons";
import { Button, Table } from "antd";

import { SANCTION_STATUS } from "@config";
import { i18, translationGroups, trb } from "@utilities";
import {
  ApplicationsActions,
  selectIsSanctionsLoading,
  selectSanctionList,
  selectCurrentApplicationId
} from "@redux";
import { ContentLoader, ContentNoData } from "@components";
import { ReviewSanctionModal } from "../Modals/ReviewSanctionModal";

export const DowJonesTab = React.memo(() => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsSanctionsLoading);
  const sanctions = useSelector(selectSanctionList);
  const appId = useSelector(selectCurrentApplicationId);

  const [expandedSanctions, setExpandedSanctions] = useState([]);
  const [selectedSanction, setSelectedSanction] = useState();

  const mountedOnce = useRef(false);

  useEffect(() => {
    if (mountedOnce.current) return;
    (async () => {
      await dispatch(ApplicationsActions.getSanctionsList(appId));
      mountedOnce.current = true;
    })();
  }, []);

  if (isLoading) {
    return <ContentLoader />;
  }

  if (sanctions?.length === 0 && !isLoading) {
    return <ContentNoData />;
  }

  const columnsSanctions = [
    {
      title: trb(i18.ApplicationsLabels.Name),
      dataIndex: "name",
      key: "name"
    },
    {
      title: trb(i18.ApplicationsLabels.Status),
      dataIndex: "status",
      key: "status",
      render: item => trb(translationGroups.SanctionStatus + item)
    },
    {
      title: trb(i18.ApplicationsLabels.Type),
      dataIndex: "type",
      key: "type",
      render: item => trb(translationGroups.SanctionType + item)
    },
    {
      title: trb(i18.ApplicationsLabels.Actions),
      key: "index",
      render: (_, item) => {
        return item.status === SANCTION_STATUS.AWAITING_REVIEW &&
          item.response.hits &&
          item.response.hits.length !== 0 ? (
          <Button size="small" onClick={() => setSelectedSanction(item)}>
            <CheckOutlined />
            {trb(i18.Buttons.ReviewSanction)}
          </Button>
        ) : null;
      },
      width: "50%"
    }
  ];

  const columnsSanctionHits = [
    {
      dataIndex: "score",
      key: "score",
      width: "20%"
    },
    {
      dataIndex: "names",
      key: "names",
      render: item => (
        <table className="eb_fs_12">
          <thead>
            <tr>
              <th className="w-50">{trb(i18.ApplicationsLabels.Name)}</th>
              <th className="w-50">{trb(i18.ApplicationsLabels.Type)}</th>
            </tr>
          </thead>
          <tbody>
            {item.map((itemName, index) => (
              <tr key={index}>
                <td>{itemName.value}</td>
                <td>{itemName.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ),
      width: "80%"
    }
  ];

  const toggleSanctionRow = (flag, sanctionId) => {
    if (!flag) {
      setExpandedSanctions(expandedSanctions.filter(sanction => sanction !== sanctionId));
      return;
    }
    setExpandedSanctions([...expandedSanctions, sanctionId]);
  };

  return (
    <>
      <div className="dowJones-tab-container">
        <Table
          className="w-100 mb-3"
          dataSource={sanctions}
          columns={columnsSanctions}
          scroll={{ x: "60vw" }}
          pagination={false}
          rowKey="id"
          expandable={{
            expandedRowRender: item => (
              <Table
                dataSource={item.response.hits}
                columns={columnsSanctionHits}
                pagination={false}
                showHeader={false}
              />
            ),
            expandedRowKeys: expandedSanctions,
            onExpand: (flag, sanction) => toggleSanctionRow(flag, sanction.id),
            rowExpandable: item => !!item.response.hits && item.response.hits.length > 0
          }}
        />
        {!!selectedSanction && (
          <ReviewSanctionModal
            onClose={() => setSelectedSanction(undefined)}
            sanction={selectedSanction}
            appId={appId}
          />
        )}
      </div>
    </>
  );
});
